/**
 * Review Session mission memory — Milestone 37.3.
 *
 * Thin helper that finds the current campaign mission for a blueprint
 * and writes the 37.3 review-session pointer keys into its memory. The
 * canonical resume_route selection is owned by the backend sync
 * service; this hook only supplies the pointers that let the backend
 * choose the review-session route.
 */
import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { advanceMission, listResumableMissions } from "./api";
import { missionKeys } from "./hooks";
import { writeLocalMission } from "./memory";
import type { Mission } from "./types";

export type ReviewMemoryPointers = {
  review_pack_id?: string | null;
  review_asset_id?: string | null;
  review_position?: number | null;
  review_total_at_pause?: number | null;
  review_paused_at?: string | null;
};

async function findCampaignMissionForBlueprint(
  workspaceId: string,
  blueprintId: string,
): Promise<Mission | null> {
  const { items } = await listResumableMissions(workspaceId, 25);
  const match = items.find(
    (m) => m.mission_type === "campaign" && m.subject_id === blueprintId,
  );
  return match ?? null;
}

/**
 * Write review-session pointers into the campaign mission memory. Best
 * effort — swallows errors so the founder's decision flow is never
 * blocked by a memory sync race.
 */
export function useWriteReviewMemory(
  workspaceId: string | null,
  userId: string | null,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      blueprintId: string;
      pointers: ReviewMemoryPointers;
    }) => {
      if (!workspaceId) return null;
      const mission = await findCampaignMissionForBlueprint(
        workspaceId,
        input.blueprintId,
      );
      if (!mission) return null;
      try {
        const patched = await advanceMission(workspaceId, mission.id, {
          expected_version: mission.version,
          memory_patch: input.pointers as Record<string, unknown>,
        });
        if (userId) writeLocalMission(userId, patched);
        return patched;
      } catch {
        // Best effort — memory sync failures are non-blocking.
        return null;
      }
    },
    onSuccess: (mission) => {
      if (!mission) return;
      qc.setQueryData(missionKeys.detail(workspaceId, mission.id), mission);
      void qc.invalidateQueries({ queryKey: missionKeys.list(workspaceId) });
    },
  });
}

/** Callable helper — read the campaign mission for a blueprint (used
 * when the page needs to know whether a session was already paused). */
export function useFindCampaignMission(workspaceId: string | null) {
  return useCallback(
    async (blueprintId: string): Promise<Mission | null> => {
      if (!workspaceId) return null;
      return findCampaignMissionForBlueprint(workspaceId, blueprintId);
    },
    [workspaceId],
  );
}
